export interface CreateFreshdeskTicketInput {
  requesterId?: number;
  requesterEmail: string;
  requesterName?: string;
  subject: string;
  descriptionHtml: string;
  status?: number;
  priority?: number;
  type?: string;
  groupId?: number;
  attachments?: File[];
}

export interface FreshdeskTicketResponse {
  id: number;
  subject: string;
  description?: string;
  description_text?: string;
  description_html?: string;
  status: number;
  priority: number;
  [key: string]: unknown;
}

export interface ListFreshdeskTicketsParams {
  email?: string;
  requester_id?: number;
  company_id?: number;
  updated_since?: string;
  page?: number;
  per_page?: number;
}

export interface FreshdeskContact {
  id: number;
  email?: string;
  name?: string;
  [key: string]: unknown;
}

export interface FreshdeskConversation {
  id: number;
  body?: string;
  body_text?: string;
  from_email?: string;
  user_id?: number;
  created_at?: string;
  attachments?: Array<{
    id: number;
    name: string;
    attachment_url?: string;
    content_type?: string;
    size?: number;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}
import { FreshdeskClient } from '@@/api/api-freshdesk';
import { ApiResult } from '@@/types/ApiResult';

const getFreshdeskClient = () => new FreshdeskClient();

interface FreshdeskError {
  code?: string;
  field?: string;
  message?: string;
}

interface FreshdeskErrorResponse {
  description?: string;
  errors?: FreshdeskError[];
}

function isApiResult<T>(result: ApiResult<T> | T): result is ApiResult<T> {
  return typeof result === 'object' && result !== null && 'ok' in result;
}

function extractData<T>(result: ApiResult<T> | T): T {
  if (isApiResult(result)) {
    if (result.ok === false) {
      throw new Error(result.message);
    }
    return result.data;
  }
  return result;
}

// Ensure we don't fire duplicate requests for the same action in quick succession
const freshdeskSingleFlight = new Map<string, Promise<FreshdeskTicketResponse>>();

/** Maximum attachment size (15MB). */
const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/** Allowed attachment file extensions for Freshdesk uploads. */
const ALLOWED_ATTACHMENT_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'tif',
  'tiff',
  'pdf',
  'doc',
  'docx',
  'rtf',
  'txt',
  'xls',
  'xlsx',
  'csv',
  'ppt',
  'pptx'
]);
function appendValidatedAttachments(form: FormData, files?: File[]) {
  for (const file of files ?? []) {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (!ALLOWED_ATTACHMENT_EXTENSIONS.has(extension)) {
      console.warn(`Skipping unsupported attachment type for Freshdesk: ${file.name}`);
      continue;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      console.warn(`Skipping oversized attachment (>15MB): ${file.name}`);
      continue;
    }
    form.append('attachments[]', file, file.name);
  }
}
/**
 * Create a Freshdesk ticket. Ensures the requester exists as a contact first.
 *
 * - When attachments are present, sends multipart/form-data.
 * - Otherwise, sends JSON.
 */
export async function createFreshdeskTicket(input: CreateFreshdeskTicketInput): Promise<FreshdeskTicketResponse> {
  const client = getFreshdeskClient();
  const hasAttachments = Array.isArray(input.attachments) && input.attachments.length > 0;
  const singleFlightKey = JSON.stringify({ op: 'createTicket', email: input.requesterEmail, subject: input.subject });

  const existing = freshdeskSingleFlight.get(singleFlightKey) as Promise<FreshdeskTicketResponse> | undefined;
  if (existing) return existing;

  const requestPromise = (async (): Promise<FreshdeskTicketResponse> => {
    // Ensure the requester exists as a Freshdesk contact before creating the ticket.
    let requesterId: number | undefined;
    let existingContact: FreshdeskContact | null = null;
    try {
      existingContact = await getFreshdeskContactByEmail(input.requesterEmail);
    } catch (err) {
      // If lookup fails due to transient issues, we'll fallback to using the email field below.
      console.warn('Failed to lookup Freshdesk contact:', err);
    }

    if (existingContact?.id) {
      requesterId = existingContact.id;
    } else {
      try {
        const created = await createFreshdeskContact({
          email: input.requesterEmail,
          name: input.requesterName
        });
        requesterId = created?.id;
      } catch (err) {
        // Parse Freshdesk error if possible to provide a clearer message
        const rawMessage = String(err instanceof Error ? err.message : 'Failed to create Freshdesk contact');
        let friendly = rawMessage;
        const jsonStart = rawMessage.indexOf('{');
        if (jsonStart !== -1) {
          try {
            const parsed = JSON.parse(rawMessage.slice(jsonStart)) as FreshdeskErrorResponse;
            if (parsed?.description) {
              const errors = Array.isArray(parsed.errors) ? parsed.errors : [];
              const duplicate = errors.find(
                (e: FreshdeskError) => e?.code === 'duplicate_value' && e?.field === 'email'
              );
              if (duplicate) {
                friendly =
                  'This email is already in use on Freshdesk (possibly as an agent). We cannot create a customer contact for it. Please use a different email or contact support.';
              } else {
                const joined = errors
                  .map((e: FreshdeskError) => e?.message)
                  .filter(Boolean)
                  .join('; ');
                friendly = `${parsed.description}${joined ? `: ${joined}` : ''}`;
              }
            }
          } catch {
            // Swallow JSON parse errors
          }
        }
        throw new Error(friendly);
      }
    }
    let body: BodyInit;

    if (hasAttachments) {
      // Multipart: append fields + validated attachments
      const form = new FormData();
      if (typeof requesterId === 'number') {
        form.append('requester_id', String(input.requesterId ?? requesterId));
      } else {
        form.append('email', input.requesterEmail);
      }
      form.append('subject', input.subject);
      if (input.requesterName) form.append('name', input.requesterName);
      // Freshdesk accepts HTML in the description field
      form.append('description', input.descriptionHtml);
      // Freshdesk requires valid status/priority; set defaults if missing
      form.append('status', String(input.status ?? 2));
      form.append('priority', String(input.priority ?? 1));

      if (input.type) form.append('type', input.type);
      if (input.groupId) form.append('group_id', String(input.groupId));

      appendValidatedAttachments(form, input.attachments);

      body = form;
    } else {
      body = JSON.stringify({
        ...(typeof requesterId === 'number' ? { requester_id: requesterId } : { email: input.requesterEmail }),
        ...(input.requesterName ? { name: input.requesterName } : {}),
        subject: input.subject,
        description: input.descriptionHtml,
        status: input.status ?? 2,
        priority: input.priority ?? 1,
        ...(input.type ? { type: input.type } : {}),
        ...(typeof input.groupId === 'number' ? { group_id: input.groupId } : {})
      });
    }
    if (hasAttachments) {
      const result = await client.proxy<FreshdeskTicketResponse>({ path: '/tickets', method: 'POST', body });
      return extractData(result);
    } else {
      const result = await client.proxy<FreshdeskTicketResponse>({
        path: '/tickets',
        method: 'POST',
        body: body
      });
      return extractData(result);
    }
  })();

  // Prevents duplicate network calls (and duplicate ticket creation) if the user triggers the same action multiple times quickly
  freshdeskSingleFlight.set(singleFlightKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    freshdeskSingleFlight.delete(singleFlightKey);
  }
}

export async function getFreshdeskTicketById(ticketId: number): Promise<FreshdeskTicketResponse> {
  const client = getFreshdeskClient();
  const result = await client.proxy<FreshdeskTicketResponse>({ path: `/tickets/${ticketId}`, method: 'GET' });
  return extractData(result);
}

export async function getFreshdeskTicketConversations(ticketId: number): Promise<FreshdeskConversation[]> {
  const client = getFreshdeskClient();
  const result = await client.proxy<FreshdeskConversation[]>({
    path: `/tickets/${ticketId}/conversations`,
    method: 'GET'
  });
  return extractData(result);
}

export async function listFreshdeskTickets(
  params: ListFreshdeskTicketsParams = {}
): Promise<FreshdeskTicketResponse[]> {
  const client = getFreshdeskClient();
  const search = new URLSearchParams();
  if (typeof params.requester_id === 'number') search.set('requester_id', String(params.requester_id));
  if (typeof params.company_id === 'number') search.set('company_id', String(params.company_id));
  if (params.updated_since) search.set('updated_since', params.updated_since);
  if (typeof params.page === 'number') search.set('page', String(params.page));
  if (typeof params.per_page === 'number') search.set('per_page', String(params.per_page));
  const query = search.toString();
  const result = await client.proxy<FreshdeskTicketResponse[]>({ path: '/tickets', method: 'GET', query });
  return extractData(result);
}

export interface FreshdeskGroup {
  id: number;
  name: string;
  [key: string]: unknown;
}

export async function listFreshdeskGroups(): Promise<FreshdeskGroup[]> {
  const client = getFreshdeskClient();
  const result = await client.proxy<FreshdeskGroup[]>({ path: '/groups', method: 'GET' });
  return extractData(result);
}

/** Search Freshdesk tickets by requester email. */
export async function searchFreshdeskTicketsByEmail(email: string, page = 1): Promise<FreshdeskTicketResponse[]> {
  const client = getFreshdeskClient();
  const queryString = `"email:'${email}'"`;
  const params = new URLSearchParams({ query: queryString, page: String(page) }).toString();
  const result = await client.proxy<{ results: FreshdeskTicketResponse[] }>({
    path: '/search/tickets',
    method: 'GET',
    query: params
  });
  const data = extractData(result);
  return Array.isArray(data?.results) ? data.results : [];
}

export async function getFreshdeskContactByEmail(email: string): Promise<FreshdeskContact | null> {
  const client = getFreshdeskClient();
  const result = await client.proxy<FreshdeskContact[]>({ path: '/contacts', method: 'GET', query: { email } });
  const contacts = extractData(result);
  if (Array.isArray(contacts) && contacts.length > 0) {
    return contacts[0];
  }
  return null;
}

export interface CreateFreshdeskContactInput {
  email: string;
  name?: string;
}

export async function createFreshdeskContact(input: CreateFreshdeskContactInput): Promise<FreshdeskContact> {
  const client = getFreshdeskClient();
  const body = { email: input.email, ...(input.name ? { name: input.name } : {}) };
  const result = await client.proxy<FreshdeskContact>({
    path: '/contacts',
    method: 'POST',
    body: JSON.stringify(body)
  });
  return extractData(result);
}

export interface CustomerReplyInput {
  ticketId: number;
  bodyHtml: string;
  customerEmail: string;
  customerName?: string;
  attachments?: File[];
}

export async function addCustomerReplyToTicket(input: CustomerReplyInput): Promise<FreshdeskConversation> {
  // Get customer contact info to associate with the reply
  let userId: number | undefined;

  try {
    const contact = await getFreshdeskContactByEmail(input.customerEmail);
    if (contact?.id) {
      userId = contact.id;
    } else {
      try {
        const newContact = await createFreshdeskContact({
          email: input.customerEmail,
          name: input.customerName || input.customerEmail.split('@')[0]
        });
        userId = newContact?.id;
      } catch (err) {
        const rawMessage = String(err instanceof Error ? err.message : 'Failed to create Freshdesk contact');
        let friendly = rawMessage;
        const jsonStart = rawMessage.indexOf('{');
        if (jsonStart !== -1) {
          try {
            const parsed = JSON.parse(rawMessage.slice(jsonStart)) as FreshdeskErrorResponse;
            if (parsed?.description) {
              const errors = Array.isArray(parsed.errors) ? parsed.errors : [];
              const duplicate = errors.find(
                (e: FreshdeskError) => e?.code === 'duplicate_value' && e?.field === 'email'
              );
              if (duplicate) {
                friendly =
                  'This email is already in use on Freshdesk (possibly as an agent). We cannot create a customer contact for it. Please use a different email or contact support.';
              } else {
                const joined = errors
                  .map((e: FreshdeskError) => e?.message)
                  .filter(Boolean)
                  .join('; ');
                friendly = `${parsed.description}${joined ? `: ${joined}` : ''}`;
              }
            }
          } catch {
            // Swallow JSON parse errors
          }
        }
        throw new Error(friendly);
      }
    }
  } catch (error) {
    console.warn('Failed to create/find Freshdesk contact:', error);
  }

  if (userId) {
    const client = getFreshdeskClient();
    const hasAttachments = Array.isArray(input.attachments) && input.attachments.length > 0;

    let body: BodyInit;
    if (hasAttachments) {
      const form = new FormData();
      // Keep multipart body structure aligned with JSON branch
      form.append('body', input.bodyHtml);
      form.append('user_id', String(userId));
      form.append('incoming', 'true');
      form.append('private', 'false');

      appendValidatedAttachments(form, input.attachments);
      body = form;
    } else {
      body = JSON.stringify({
        body: input.bodyHtml,
        user_id: userId,
        incoming: true,
        private: false
      });
    }

    const endpoint = `/tickets/${input.ticketId}/notes`;
    const result = await client.proxy<FreshdeskConversation>({
      path: endpoint,
      method: 'POST',
      body: body instanceof FormData ? (body as FormData) : body
    });
    return extractData(result);
  } else {
    // If contact creation failed for a strong reason (like duplicate email tied to an agent), surface the error
    // instead of posting as a generic reply.
    throw new Error('Unable to locate or create a Freshdesk contact for this email.');
  }
}
